import { Component, Input } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { PonderAuthClient } from "@apiserver-rest/ponder-auth-client";
import { AuthResponse, UpdateUsernameRequest } from "@apiserver-rest/ponder-auth-client-base";
import { AuthSession } from "@ponder-core/auth/auth-session";
import { AuthService } from "@ponder-core/auth/auth.service";
import { NavigationService } from "@ponder-core/navigation/navigation.service";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { RateLimitedError } from "@ponder-shared/errors/rate-limited-error";
import { take } from "rxjs";

@Component({
    selector: "user-username-change",
    imports: [ReactiveFormsModule, BusySpinComponent],
    templateUrl: "./user-account-username-change.component.html"
})
export class UserAccountUsernameChangeComponent {
    @Input({ required: true }) public authSession?: AuthSession;

    public updateUsernameForm!: FormGroup;
    public isUpdatingUsername: boolean = false;
    public updatingUsernameError?: string;
    public requireConfirmation: boolean = false;

    private currUsername?: string;
    private hasUsernameChanged: boolean = false;
    private isThrottled: boolean = false;

    public constructor(formBuilder: FormBuilder, private ponderAuthClient: PonderAuthClient, private authService: AuthService, private navigation: NavigationService) {
        this.updateUsernameForm = formBuilder.group({ 
            username: ["", [Validators.required, Validators.minLength(3), Validators.maxLength(64)]]
        });
        
        this.updateUsernameForm.valueChanges.subscribe(() => { this.onUsernameChanged() });
    }

    public ngOnChanges(): void {
        if (!this.authSession) {
            return;
        }

        this.currUsername = this.authSession.username;
        this.updateUsernameForm.get("username")!.setValue(this.authSession.username);
    }
    
    private onUsernameChanged(): void {
        this.hasUsernameChanged = (this.currUsername != this.updateUsernameForm.get("username")!.value);
    }

    public canSubmitUsernameChange(): boolean {
        return this.updateUsernameForm.valid && this.hasUsernameChanged && !this.isUpdatingUsername && !this.requireConfirmation && !this.isThrottled;
    }

    public onChangeUsernameClicked(): void {
        if (!this.canSubmitUsernameChange()) {
            return;
        }

        this.requireConfirmation = true;
        this.updateUsernameForm.disable();
    }

    public onCancelChangeUsernameClicked(): void {
        this.requireConfirmation = false;
        this.updateUsernameForm.get("username")?.setValue(this.currUsername);
        this.updateUsernameForm.enable();
    }

    public onConfirmChangeUsernameClicked(): void {
        if (this.isUpdatingUsername) {
            return;
        }
        
        this.requireConfirmation = false;
        this.isUpdatingUsername = true;
        this.updatingUsernameError = undefined;
        this.updateUsernameForm.disable();

        const request = new UpdateUsernameRequest({
            username: this.updateUsernameForm.get("username")!.value
        });

        this.ponderAuthClient
            .accountUsername(request)
            .pipe(take(1))
            .subscribe({
                next: (response: AuthResponse) => {
                    if (response.success) {
                        this.authService.changeUsernameAsync(request.username).finally(() => {
                            this.navigation.goto("/account");
                        });
                    } else {
                        this.updatingUsernameError = response.error;
                        this.updateUsernameForm.enable({ emitEvent: false });
                    }
                },
                error: (e: any) => {
                    if (e.status == 429) {
                        var rateLimitedError = JSON.parse(e.response) as RateLimitedError;
                        this.isThrottled = true;
                        this.updatingUsernameError =`Slow down! You have reached your daily limit (${rateLimitedError.limit}) on username changes.`;
                    } else {
                        this.updatingUsernameError = "Unknown error.";
                        this.updateUsernameForm.enable({ emitEvent: false });
                    }
                }
            })
            .add(() => {
                this.isUpdatingUsername = false;
            });
    }
}